import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, MinusCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import TopBar from "../components/TopBar";
import CefrBadge from "../components/CefrBadge";
import B1Verdict from "../components/B1Verdict";
import SpeakingRecordings from "../components/SpeakingRecordings";
import { SpeakingScoreEditor } from "../components/admin/SpeakingScoreEditor";
import { DecisionPanel } from "../components/admin/DecisionPanel";
import { formatDateTime, formatDuration } from "../lib/cefr";

const SECTIONS = ["grammar", "vocabulary", "reading", "listening"];
const letter = (i) => String.fromCharCode(65 + i);

function status(it) {
  const empty = it.selected === null || it.selected === undefined || it.selected === "";
  if (it.is_correct) return "right";
  return empty ? "blank" : "wrong";
}

function StatusIcon({ s }) {
  if (s === "right") return <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" />;
  if (s === "wrong") return <XCircle size={20} weight="fill" className="text-red-600 shrink-0" />;
  return <MinusCircle size={20} weight="fill" className="text-muted-foreground shrink-0" />;
}

function Item({ it }) {
  const s = status(it);
  return (
    <div className="py-5 border-b border-foreground/15" data-testid={`review-item-${it.id}`} data-status={s}>
      <div className="flex items-start gap-3">
        <StatusIcon s={s} />
        <div className="flex-1 min-w-0">
          <div className="overline">
            {it.number ? `Q${it.number} · ` : ""}{it.part_title || it.section} {it.difficulty ? `· ${it.difficulty}` : ""}
          </div>
          <p className="mt-1 font-bold text-foreground whitespace-pre-line">{it.prompt}</p>
          {it.snippet && <p className="mt-2 text-sm text-foreground/70 italic whitespace-pre-line">{it.snippet}</p>}
          {it.type === "text" ? (
            <div className="mt-3 text-sm space-y-1">
              <div>Answer: <span className={`font-mono-fig font-bold ${s === "right" ? "text-emerald-600" : "text-red-600"}`}>{it.selected || "— (blank)"}</span></div>
              <div className="text-muted-foreground">Accepted: <span className="font-mono-fig">{(it.accepted || [it.correct]).join(", ")}</span></div>
            </div>
          ) : it.options ? (
            <ul className="mt-3 space-y-1">
              {it.options.map((o, i) => {
                const isCorrect = i === it.correct;
                const isChosen = i === it.selected;
                return (
                  <li
                    key={i}
                    className={`flex items-start gap-3 px-3 py-1.5 text-sm border ${
                      isCorrect ? "border-emerald-600 bg-emerald-600/10" : isChosen ? "border-red-600 bg-red-600/10" : "border-transparent"
                    }`}
                  >
                    <span className="font-mono-fig font-bold w-4">{letter(i)}</span>
                    <span className="flex-1">{o}</span>
                    {isChosen && <span className="overline !text-current">their answer</span>}
                    {isCorrect && !isChosen && <span className="overline !text-emerald-600">correct</span>}
                  </li>
                );
              })}
              {s === "blank" && <li className="text-xs text-muted-foreground px-3">Not answered</li>}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function WritingBlock({ title, subtitle, text, task }) {
  return (
    <div className="neu p-6" data-testid={`review-writing-${task?.id || title}`}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-lg font-bold">{title}</h3>
        {task && <span className="font-mono-fig whitespace-nowrap">{task.score}<span className="text-muted-foreground text-xs"> / {task.max}</span></span>}
      </div>
      {subtitle && <p className="mt-1 text-sm italic text-foreground/75" data-testid="review-question-text">{subtitle}</p>}
      <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{text || "— (no response)"}</p>
      {task?.checks && (
        <ul className="mt-4 space-y-1 text-sm">
          {task.checks.map((c) => (
            <li key={c.label} className="flex items-center gap-2">
              {c.met ? <CheckCircle size={15} weight="fill" className="text-emerald-600" /> : <XCircle size={15} weight="fill" className="text-red-600" />}
              {c.label}
            </li>
          ))}
        </ul>
      )}
      {task?.notes && <p className="mt-3 text-xs text-muted-foreground">{task.notes} · {task.word_count} words</p>}
      {task?.criteria && (
        <ul className="mt-3 space-y-2 text-xs border-t border-foreground/15 pt-3" data-testid="review-ai-criteria">
          {task.criteria.map((c) => (
            <li key={c.key}><span className="font-bold">{c.label} {c.score}/{c.max}:</span> <span className="text-foreground/75">{c.comment}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminAttemptReview() {
  const { attemptId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get(`/admin/attempts/${attemptId}/review`).then((r) => setData(r.data)).catch(() => setError("Result not found."));
  }, [attemptId]);

  const counts = useMemo(() => {
    const c = { right: 0, wrong: 0, blank: 0 };
    (data?.items || []).forEach((it) => { c[status(it)] += 1; });
    return c;
  }, [data]);

  if (error) return <div className="min-h-screen"><TopBar /><p className="p-10 text-center" data-testid="review-error">{error}</p></div>;
  if (!data) return <div className="min-h-screen"><TopBar /><p className="p-10 text-center font-mono-fig text-muted-foreground">Loading…</p></div>;

  const tasks = data.writing_feedback?.tasks || [];
  const shown = (section) => data.items.filter((it) => it.section === section && (filter === "all" || status(it) === filter));

  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-attempt-review">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-bold hover:text-signal" data-testid="review-back-link">
          <ArrowLeft size={14} weight="bold" /> Back to admin
        </Link>

        <div className="mt-6 pb-8 border-b border-foreground flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="overline">Test review</div>
            <h1 className="text-4xl md:text-5xl mt-2 font-bold" data-testid="review-user-name">{data.user?.name || "Deleted user"}</h1>
            <p className="mt-2 text-sm text-muted-foreground font-mono-fig">
              {data.user?.email} {data.user?.whatsapp_number ? `· ${data.user.whatsapp_number}` : ""}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{formatDateTime(data.created_at)} · {formatDuration(data.duration_seconds)}</p>
          </div>
          <div className="flex items-end gap-4">
            <div className="numeral text-7xl" data-testid="review-score">{Math.round(data.total_score)}</div>
            <div className="pb-2 space-y-2">
              <div className="font-mono-fig text-muted-foreground">/ {data.total_max}</div>
              <CefrBadge level={data.cefr_level} size="sm" />
            </div>
            <div className="pb-2">
              <B1Verdict score={data.total_score} testId="review-b1-verdict" />
            </div>
          </div>
        </div>

        <DecisionPanel attemptId={attemptId} initial={data.admin_decision} />

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.entries(data.section_scores || {}).map(([k, v]) => (
            <div key={k} className="neu p-4">
              <div className="overline">{k}</div>
              <div className="font-mono-fig text-2xl mt-1">{v}<span className="text-xs text-muted-foreground"> / {data.section_max?.[k]}</span></div>
              {data.section_correct?.[k] !== undefined && (
                <div className="text-xs text-muted-foreground mt-1">
                  {data.section_correct[k]} of {data.section_counts?.[k]} correct
                  {data.section_wrong?.[k] ? ` · ${data.section_wrong[k]} wrong` : ""}
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground max-w-3xl" data-testid="review-scoring-note">
          Scoring: each right answer = 1 point. A wrong multiple-choice answer subtracts a fraction (1/2 with 3 options, 1/3 with 4) so
          blind guessing averages zero; blank and typed answers never lose points. Writing only earns points for the parts of the task
          covered. Speaking is your spoken description of the picture, checked against the admin's parameters. Levels: A1 0–39 · A2 40–69 · B1 70–79 · B2 80–89 · C1 90–95 · C2 96–100.
        </p>

        <div className="mt-8 flex flex-wrap gap-2" role="tablist">
          {[["all", `All (${data.items.length})`], ["right", `Right (${counts.right})`], ["wrong", `Wrong (${counts.wrong})`], ["blank", `Blank (${counts.blank})`]].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setFilter(id)}
              data-testid={`review-filter-${id}`}
              className={`px-4 py-2 text-sm font-bold border ${filter === id ? "bg-foreground text-background border-foreground" : "neu-btn"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Options are listed in their original order; the learner may have seen them shuffled.</p>

        {data.items.length === 0 && <p className="mt-8 text-sm text-muted-foreground">No question details were stored for this result.</p>}

        {SECTIONS.map((section) => {
          const list = shown(section);
          if (!list.length) return null;
          return (
            <section key={section} className="mt-10" data-testid={`review-section-${section}`}>
              <h2 className="text-2xl font-bold capitalize pb-3 border-b border-foreground">{section}</h2>
              {list.map((it) => <Item key={it.id} it={it} />)}
            </section>
          );
        })}

        {filter === "all" && (
          <section className="mt-10" data-testid="review-section-writing">
            <h2 className="text-2xl font-bold pb-3 border-b border-foreground">Writing</h2>
            <div className="mt-6 grid lg:grid-cols-2 gap-4">
              <WritingBlock title="Task 1 · Email" text={data.writing?.email} task={tasks.find((t) => t.id === "email")} />
              <WritingBlock
                title={`Task 2 · ${data.writing?.choice === "story" ? "Story" : "Article"}`}
                text={data.writing?.extended}
                task={tasks.find((t) => t.id !== "email")}
              />
              {data.speaking_feedback && (
                <WritingBlock title="Speaking · what they said" text={data.speaking_feedback.transcript} task={data.speaking_feedback} />
              )}
              {data.speaking_question_feedback && (
                <WritingBlock
                  title={`Speaking · Open question · ${data.speaking_question_feedback.method === "ai" ? "AI-marked" : "rule-based"}`}
                  subtitle={data.speaking_question_feedback.question}
                  text={data.speaking_question_feedback.transcript}
                  task={data.speaking_question_feedback}
                />
              )}
              <SpeakingRecordings attemptId={attemptId} recordings={data.speaking_recordings} title="Speaking · listen to every take" />
              {data.section_scores?.speaking !== undefined && (
                <SpeakingScoreEditor
                  attemptId={attemptId}
                  current={data.section_scores.speaking}
                  max={data.section_max?.speaking ?? 20}
                  override={data.speaking_override}
                  onChange={(r) => setData((d) => ({
                    ...d, ...r,
                    speaking_feedback: d.speaking_feedback && !d.speaking_question_feedback ? { ...d.speaking_feedback, score: r.section_scores.speaking } : d.speaking_feedback,
                  }))}
                />
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
